/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import { useLocation, useNavigate } from 'react-router-dom';
import Badge from 'src/components/ui/Badge/Badge';
import { Tooltip as MuiTooltip } from '@mui/material';
import styles from './NavItem.module.scss';
import { TextBadge } from '../../../ui/TextBadge/TextBadge';

enum BadgeType {
  Neutral = 'neutral',
  Successful = 'succesful',
  Warning = 'warning',
  Error = 'error',
  Neutral2 = 'neutral2',
  Neutral3 = 'neutral3',
  Neutral4 = 'neutral4',
  Neutral5 = 'neutral5',
}

const SIDEBAR_WARN = '#F2B935';
const SIDEBAR_ALERT = '#F07068';
const SIDEBAR_MUTED = '#B8BEC9';

export type NavItemProps = {
  link?: string;
  icon?: IconName;
  label: string;
  count?: number;
  count_info?: number;
  count_todo?: number;
  description?: string;
  description_info?: string;
  description_todo?: string;
  badgeType?: BadgeType;
  badgeLabel?: string;
  badgeIcon?: IconName;
  expanded?: boolean;
  onClick?: () => void;
  shortcutText?: string;
  isError?: boolean;
  inactive?: boolean;
  childs?: NavItemProps[];
  linkWithDomain?: string;
  is_beta?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types -- menu data read by NavItemList to group the sidebar
  section?: string;
};

const NavItem: React.FC<NavItemProps> = ({
  link = '',
  linkWithDomain,
  label,
  badgeType,
  badgeLabel,
  badgeIcon,
  icon,
  expanded,
  onClick,
  shortcutText,
  isError,
  inactive,
  childs = [],
  count,
  description,
  count_info,
  count_todo,
  description_info,
  description_todo,
  is_beta = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isActive = location.pathname === `/${link}` && childs?.length === 0;

  const translation = t(label);
  const navigate = useNavigate();

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (linkWithDomain) {
      navigate(`/${linkWithDomain}`);
    }
  };

  const handleLinkClick = () => {
    if (onClick) {
      onClick();
    }
    if (link) {
      if (childs.length > 0) {
        navigate(`/${childs[0].link}`);
      } else {
        navigate(`/${link}`);
      }
    }
  };

  const infoCount = count_info ?? 0;
  const todoCount = count_todo ?? 0;
  const sidebarBadgeColor = (type?: BadgeType) => {
    if (type === BadgeType.Error) return SIDEBAR_ALERT;
    if (type === BadgeType.Warning) return SIDEBAR_WARN;
    return SIDEBAR_MUTED;
  };

  const hasDualCounts = count_info !== undefined || count_todo !== undefined;
  const hasSingleCount = count !== undefined;

  const item = (
    <div
      className={classNames(`${styles.root} py-8 flex rounded-base cursor-pointer`, {
        'text-selected surface-selected--dark active': isActive && !inactive,
        'text-sidebar hover:text-full--dark hover:surface-elevated--dark': !isActive || inactive,
        'justify-between px-12': expanded,
        'justify-center aspect-square': !expanded,
      })}
      onClick={() => handleLinkClick()}>
      <div className="flex items-center">
        {icon && <Icon height={20} width={20} name={icon} className={`${isError && 'error'}`} />}
        {expanded && <span className={`${isError && 'error'} typo-text-14 ${icon ? 'pl-8' : ''}`}>{translation}</span>}
      </div>
      {hasSingleCount && !hasDualCounts && (
        <span className={classNames(!expanded && styles.absoluteBadge)} onClick={handleBadgeClick}>
          {description && (
            <MuiTooltip
              key={description}
              title={description}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                },
              }}>
              <div className={classNames(styles.root)}>
                <Badge
                  expanded={expanded}
                  label={String(count ?? 0)}
                  type={badgeType}
                  colorOverride={sidebarBadgeColor(badgeType)}
                />
              </div>
            </MuiTooltip>
          )}
        </span>
      )}

      {hasDualCounts && (
        <span className={classNames(!expanded && styles.absoluteBadge)} onClick={handleBadgeClick}>
          <div className={`${classNames(styles.root)} gap-4`}>
            {infoCount !== undefined && description_info && (
              <MuiTooltip
                key={description_info}
                title={description_info}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: { offset: [0, -10] },
                      },
                    ],
                  },
                }}>
                <div>
                  <Badge
                    expanded={expanded}
                    label={String(infoCount)}
                    type={BadgeType.Warning}
                    colorOverride={SIDEBAR_WARN}
                  />
                </div>
              </MuiTooltip>
            )}

            {todoCount !== undefined && description_todo && todoCount > 0 && (
              <MuiTooltip
                key={description_todo}
                title={description_todo}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: { offset: [0, -10] },
                      },
                    ],
                  },
                }}>
                <div>
                  <Badge
                    expanded={expanded}
                    label={String(todoCount)}
                    type={BadgeType.Error}
                    colorOverride={SIDEBAR_ALERT}
                  />
                </div>
              </MuiTooltip>
            )}
          </div>
        </span>
      )}

      {badgeType && (badgeLabel || badgeIcon) && (
        <span className={classNames(styles.absoluteBadge)}>
          <Badge
            expanded={expanded}
            label={badgeLabel || ''}
            type={badgeType}
            colorOverride={sidebarBadgeColor(badgeType)}
            icon={expanded ? badgeIcon : undefined}
          />
        </span>
      )}
      {is_beta && !(badgeType && (badgeLabel || badgeIcon)) && (
        <span className={classNames(styles.absoluteBadge)}>
          {expanded ? (
            <TextBadge
              type={BadgeType.Warning}
              text="BETA"
              transparent
              className="text-sidebar border-distinct--dark"
            />
          ) : (
            <Badge expanded={false} label="BETA" type={BadgeType.Warning} colorOverride={SIDEBAR_MUTED} />
          )}
        </span>
      )}
      {shortcutText && expanded && <span className="text-sidebar-muted">{shortcutText}</span>}
    </div>
  );

  if (expanded) {
    return item;
  }

  return (
    <MuiTooltip title={translation} placement="right">
      {item}
    </MuiTooltip>
  );
};

export default NavItem;
